import React, { useState, useEffect, useRef } from 'react';
import AnalyticsService from '../../services/AnalyticsService';
import { auth } from '../../firebase';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface YouTubePlayerProps {
  currentSong: any;
  onVideoEnd: () => void;
  showSplash: boolean;
  videoDuration?: number;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({ 
  currentSong, 
  onVideoEnd, 
  showSplash, 
  videoDuration 
}) => {
  const autoplaySessionKey = 'qroke:youtubeAutoplayUnlocked';
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const showSplashRef = useRef(showSplash);
  const [videoError, setVideoError] = useState(false);
  const [videoStartTime, setVideoStartTime] = useState<number | null>(null);
  const [playerReady, setPlayerReady] = useState(false);
  const videoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const playerInstanceRef = useRef<any>(null);
  const hasAdvancedRef = useRef<boolean>(false);
  const hasAutoReportedRef = useRef<boolean>(false);
  const hasStartedPlaybackRef = useRef<boolean>(false);
  const retryCountRef = useRef<number>(0);
  const [retryNonce, setRetryNonce] = useState<number>(0);
  const [needsUserStart, setNeedsUserStart] = useState<boolean>(false);
  const [extVolume, setExtVolume] = useState<number>(100);
  const [extMuted, setExtMuted] = useState<boolean>(false);
  const [autoplayUnlocked, setAutoplayUnlocked] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('qroke:youtubeAutoplayUnlocked') === '1';
    } catch {
      return false;
    }
  });

  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

  const clearStartupTimeout = () => {
    if (startupTimeoutRef.current) {
      clearTimeout(startupTimeoutRef.current);
      startupTimeoutRef.current = null;
    }
  };

  const armStartupFallback = () => {
    clearStartupTimeout();
    startupTimeoutRef.current = setTimeout(() => {
      if (!hasStartedPlaybackRef.current) {
        setNeedsUserStart(true);
      }
    }, 4000);
  };

  const probeAutoplay = (player: any) => {
    try {
      armStartupFallback();
      player.mute();
      player.playVideo();
      setTimeout(() => {
        const state = player?.getPlayerState?.();
        // 1 = PLAYING
        if (state !== 1) {
          setNeedsUserStart(true);
        } else {
          clearStartupTimeout();
          // Keep desktop unmuted when playback is confirmed
          if (!isMobile) {
            try { player.unMute(); } catch {}
          }
        }
      }, 800);
    } catch {
      setNeedsUserStart(true);
    }
  };

  const rememberAutoplayUnlocked = () => {
    setAutoplayUnlocked(true);
    try {
      sessionStorage.setItem(autoplaySessionKey, '1');
    } catch {}
  };

  useEffect(() => {
    showSplashRef.current = showSplash;
  }, [showSplash]);

  // Load YouTube IFrame API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }

      window.onYouTubeIframeAPIReady = () => {
        setPlayerReady(true);
      };
    } else {
      setPlayerReady(true);
    }
  }, []);

  useEffect(() => {
    const onUserGesture = () => {
      if (autoplayUnlocked) return;
      rememberAutoplayUnlocked();
      const player = playerInstanceRef.current;
      if (player && !showSplash && !hasStartedPlaybackRef.current) {
        probeAutoplay(player);
      }
    };

    window.addEventListener('pointerdown', onUserGesture, true);
    window.addEventListener('keydown', onUserGesture, true);
    return () => {
      window.removeEventListener('pointerdown', onUserGesture, true);
      window.removeEventListener('keydown', onUserGesture, true);
    };
  }, [autoplayUnlocked, showSplash, currentSong?.videoId]);

  // Host-side external volume/mute control, driven by localStorage
  useEffect(() => {
    const partyId = (currentSong && currentSong.partyId) || (currentSong && currentSong.party) || null;
    const volKey = partyId ? `qroke:ytv:${partyId}:volume` : null;
    const muteKey = partyId ? `qroke:ytv:${partyId}:muted` : null;
    const apply = () => {
      try {
        if (volKey) {
          const v = localStorage.getItem(volKey);
          if (v !== null) setExtVolume(Math.max(0, Math.min(100, Number(v))));
        }
        if (muteKey) {
          const m = localStorage.getItem(muteKey);
          if (m !== null) setExtMuted(m === '1');
        }
      } catch {}
    };
    apply();
    const onStorage = (e: StorageEvent) => {
      if (!partyId) return;
      if (e.key === volKey || e.key === muteKey) {
        apply();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [currentSong?.partyId, currentSong?.party]);

  // Clear timeout and destroy player when component unmounts or song changes
  useEffect(() => {
    setVideoError(false);
    hasAdvancedRef.current = false;
    hasAutoReportedRef.current = false;
    hasStartedPlaybackRef.current = false;
    retryCountRef.current = 0;
    // Only destroy player when the actual song (videoId) changes, not on splash state changes
    const currentVideoId = currentSong?.videoId;
    const existingVideoId = playerInstanceRef.current?.getVideoData?.()?.video_id;
    
    if (playerInstanceRef.current && currentVideoId && existingVideoId && currentVideoId !== existingVideoId) {
      try {
        playerInstanceRef.current.destroy();
        playerInstanceRef.current = null;
      } catch (e) {
        // Error destroying player
      }
    }
    
    return () => {
      if (videoTimeoutRef.current) {
        clearTimeout(videoTimeoutRef.current);
      }
      if (startupTimeoutRef.current) {
        clearTimeout(startupTimeoutRef.current);
      }
      if (playerInstanceRef.current) {
        try {
          playerInstanceRef.current.destroy();
        } catch (e) {
          // Error destroying player on cleanup
        }
      }
    };
  }, [currentSong?.videoId]); // Only depend on videoId, not showSplash

  const safeAdvance = () => {
    if (hasAdvancedRef.current) return;
    hasAdvancedRef.current = true;
    onVideoEnd();
  };

  const markVideoAsBlocked = (videoId?: string, reason?: unknown) => {
    if (!videoId) return;
    try {
      const key = 'qroke:blockedVideoIds';
      const existing = JSON.parse(localStorage.getItem(key) || '{}');
      existing[videoId] = {
        blockedAt: Date.now(),
        reason: String(reason || 'player_error'),
        title: currentSong?.title || 'Unknown Title'
      };
      localStorage.setItem(key, JSON.stringify(existing));
    } catch {}
  };

  const isBlockedVideo = (videoId?: string): boolean => {
    if (!videoId) return false;
    try {
      const key = 'qroke:blockedVideoIds';
      const existing = JSON.parse(localStorage.getItem(key) || '{}');
      return !!existing[videoId];
    } catch {
      return false;
    }
  };

  const autoReportPlaybackFailure = async (reason?: unknown) => {
    const videoId = currentSong?.videoId;
    if (!videoId || hasAutoReportedRef.current) return;
    hasAutoReportedRef.current = true;
    const userEmail = auth.currentUser?.email || undefined;
    const details = `YouTube playback error: ${String(reason ?? 'unknown')}`;
    await AnalyticsService.markPlaybackFailed(
      videoId,
      currentSong?.title || 'Unknown Title',
      details,
      userEmail || 'Auto Player',
      auth.currentUser?.uid
    );
  };

  // Create YouTube player when conditions are met
  useEffect(() => {
    const isYouTubeSong = currentSong && currentSong.videoId && currentSong.videoId.length === 11;

    if (isYouTubeSong && isBlockedVideo(currentSong?.videoId)) {
      setVideoError(true);
      setTimeout(() => safeAdvance(), 300);
      return;
    }
    
    // Only create player if we don't have one for this song yet
    if (isYouTubeSong && currentSong && playerReady && !videoError && !playerInstanceRef.current && playerContainerRef.current) {
      // Clear any existing timeout
      if (videoTimeoutRef.current) {
        clearTimeout(videoTimeoutRef.current);
      }

      try {
        playerInstanceRef.current = new window.YT.Player(playerContainerRef.current, {
          height: '100%',
          width: '100%',
          videoId: currentSong.videoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            modestbranding: 1,
            rel: 0,
            fs: 1,
            cc_load_policy: 0,
            iv_load_policy: 3,
            autohide: 0,
            playsinline: 1,
            origin: window.location.origin,
            widget_referrer: window.location.href
          },
          events: {
            onReady: (event: any) => {
              setVideoStartTime(Date.now());
              clearStartupTimeout();
              try {
                if (typeof extVolume === 'number') event.target.setVolume(extVolume);
                if (extMuted) event.target.mute(); else event.target.unMute();
              } catch {}
              try {
                event.target.cueVideoById(currentSong.videoId);
              } catch {}
              if (!showSplashRef.current && autoplayUnlocked) {
                probeAutoplay(event.target);
              }
            },
            onStateChange: (event: any) => {
              if (event.data === 1) { // YouTube PlayerState.PLAYING
                hasStartedPlaybackRef.current = true;
                setNeedsUserStart(false);
                clearStartupTimeout();
              }
              if (showSplashRef.current && event.data === 1) {
                try {
                  event.target.pauseVideo();
                  hasStartedPlaybackRef.current = false;
                } catch {}
                return;
              }
              // YT.PlayerState.ENDED = 0
              if (event.data === 0) { // YouTube PlayerState.ENDED
                if (!hasStartedPlaybackRef.current) {
                  console.warn('[YouTubePlayer] Ignoring premature ENDED before PLAYING', {
                    videoId: currentSong?.videoId,
                    title: currentSong?.title,
                  });
                  return;
                }
                safeAdvance();
              }
            },
            onError: (event: any) => {
              clearStartupTimeout();
              handleVideoError(event?.data);
            }
          }
        });

        // Fallback timeout in case the API events don't fire
        if (videoDuration) {
          videoTimeoutRef.current = setTimeout(() => {
            safeAdvance();
          }, (videoDuration + 10) * 1000); // Add 10 seconds buffer
        }

      } catch (error) {
        handleVideoError();
      }
    }
  }, [currentSong?.videoId, playerReady, videoDuration, onVideoEnd, videoError, retryNonce, autoplayUnlocked]);

  useEffect(() => {
    const player = playerInstanceRef.current;
    if (!player || !currentSong?.videoId) return;
    if (showSplash) {
      clearStartupTimeout();
      setNeedsUserStart(false);
      try {
        player.pauseVideo();
      } catch {}
      return;
    }
    if (!hasStartedPlaybackRef.current && autoplayUnlocked) {
      probeAutoplay(player);
    }
  }, [showSplash, currentSong?.videoId, autoplayUnlocked]);

  // Apply volume/mute changes to the player on update
  useEffect(() => {
    const p = playerInstanceRef.current;
    if (!p) return;
    try {
      if (typeof extVolume === 'number') p.setVolume(extVolume);
      if (extMuted) p.mute(); else p.unMute();
    } catch {}
  }, [extVolume, extMuted]);

  // Handle video load error
  const handleVideoError = (reason?: unknown) => {
    console.error('[YouTubePlayer] Playback error', {
      videoId: currentSong?.videoId,
      title: currentSong?.title,
      reason,
      retryCount: retryCountRef.current,
    });

    if (retryCountRef.current < 1) {
      retryCountRef.current += 1;
      try {
        if (videoTimeoutRef.current) {
          clearTimeout(videoTimeoutRef.current);
        }
        clearStartupTimeout();
        if (playerInstanceRef.current) {
          playerInstanceRef.current.destroy();
          playerInstanceRef.current = null;
        }
      } catch {}
      hasStartedPlaybackRef.current = false;
      setNeedsUserStart(false);
      setRetryNonce((n) => n + 1);
      return;
    }

    markVideoAsBlocked(currentSong?.videoId, reason);
    void autoReportPlaybackFailure(reason);
    setVideoError(true);
    // Auto-advance after 5 seconds if video fails to load
    setTimeout(() => {
      safeAdvance();
    }, 5000);
  };

  if (!currentSong) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-2xl text-white">No song playing</p>
      </div>
    );
  }

  const isYouTubeSong = currentSong.videoId && currentSong.videoId.length === 11;

  if (videoError) {
    return (
      <div className="text-center p-10">
        <h2 className="text-red-400 text-2xl mb-5">Video Unavailable</h2>
        <p className="text-xl mb-2">{currentSong.title}</p>
        <p className="text-gray-400 mb-5">This video cannot be played. Advancing to next song in 5 seconds...</p>
        <button 
          onClick={onVideoEnd}
          className="mt-5 px-5 py-2 text-lg bg-qr-primary text-white border-none rounded cursor-pointer hover:bg-qr-secondary transition-colors"
        >
          Skip Now
        </button>
      </div>
    );
  }

  if (isYouTubeSong) {
    return (
      <div className="w-full h-full relative">
        <div 
          ref={playerContainerRef}
          className="w-full h-full bg-black"
        />
        {/* Streaming policy note */}
        <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] md:text-xs px-2 py-1 rounded">
          Streaming for personal/home use only. <a href="/content-sources" className="underline">Learn more</a>
        </div>
        {needsUserStart && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-10">
            <div className="max-w-md rounded-2xl bg-black/80 px-6 py-5 text-center shadow-xl">
              <p className="mb-3 text-xl font-semibold text-white">Tap once to enable autoplay</p>
              <p className="mb-5 text-sm text-gray-300">
                After this one tap, later songs should start automatically on this display for the rest of this session.
              </p>
              <button
                className="bg-qr-primary hover:bg-qr-secondary text-white px-8 py-4 rounded-xl text-lg font-semibold shadow-lg"
                onClick={() => {
                  try {
                    rememberAutoplayUnlocked();
                    setNeedsUserStart(false);
                    const p = playerInstanceRef.current;
                    if (p) {
                      p.mute();
                      p.playVideo();
                      setTimeout(() => { try { p.unMute(); } catch {} }, 600);
                    }
                  } catch {}
                }}
              >
                Enable Autoplay
              </button>
            </div>
          </div>
        )}
        {/* Video duration display for debugging */}
        {videoDuration && videoStartTime && (
          <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
            Duration: {Math.floor(videoDuration / 60)}:{(videoDuration % 60).toString().padStart(2, '0')}
          </div>
        )}
      </div>
    );
  }

  // For non-YouTube songs (local files or other sources): show a clear host-only message
  const isLocalSource = (currentSong as any)?.source === 'local';
  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="text-center bg-black/50 rounded-lg p-8 max-w-3xl mx-auto">
        <h3 className="text-2xl font-bold text-white mb-2">{currentSong.title}</h3>
        {currentSong.artist && <div className="text-lg text-gray-300 mb-4">{currentSong.artist}</div>}
        <div className="text-gray-200 text-base">
          {isLocalSource ? (
            <>
              <p>Host is playing a local library track.</p>
              <p className="text-sm text-gray-400 mt-2">Media plays on the host device to keep everything in sync.</p>
            </>
          ) : (
            <>
              <p>Content is unavailable for embedded playback.</p>
              <p className="text-sm text-gray-400 mt-2">Please wait or skip to the next song.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default YouTubePlayer;